'use client';

import { useEffect, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';

import { SigninForm } from '@/components/signin-form';
import { Spinner } from '@/components/ui/spinner';
import { useLandingTranslations } from '@/hooks/useLandingTranslations';
import { normalizeLocale } from '@/i18n/config';
import { getAuthenticatedPhone, isAuthenticated, clearAllAuthSessions } from '@/lib/auth';
import { resolveAffiliateEntryPath } from '@/lib/affiliateEntryPath';

export default function AffiliatePanelLoginPage() {
  const router = useRouter();
  const params = useParams();
  const { t } = useLandingTranslations();
  const normalizedLocale = normalizeLocale((params?.locale as string) || 'pt-BR');
  const [checked, setChecked] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const goToAffiliateEntry = async () => {
    setRedirecting(true);
    try {
      const path = await resolveAffiliateEntryPath(normalizedLocale);
      router.replace(path);
    } catch {
      router.replace(`/${normalizedLocale}/affiliate-panel`);
    }
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!isAuthenticated()) {
        if (!cancelled) setChecked(true);
        return;
      }
      const phone = getAuthenticatedPhone();
      if (!phone || phone.startsWith('__uid__')) {
        if (!cancelled) setChecked(true);
        return;
      }

      try {
        const path = await resolveAffiliateEntryPath(normalizedLocale);
        if (path.endsWith('/login')) {
          await clearAllAuthSessions();
          if (!cancelled) setChecked(true);
          return;
        }
        if (!cancelled) {
          setRedirecting(true);
          router.replace(path);
        }
      } catch {
        if (!cancelled) setChecked(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [normalizedLocale, router]);

  if (!checked || redirecting) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-background p-6 md:p-10">
        <Spinner className="size-6" />
      </div>
    );
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-background p-6 md:p-10">
      <div className="w-full max-w-sm">
        <SigninForm
          whatsappOnly
          title={t('affiliatePortal.login.title')}
          subtitle={t('affiliatePortal.login.subtitle')}
          onLoginSuccessWithoutRedirect={() => {
            void goToAffiliateEntry();
          }}
        />
      </div>
    </div>
  );
}
